import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import DriverNav from './DriverNav';
import { HiTruck, HiCalendar, HiExclamationCircle } from 'react-icons/hi';

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';

const Field = ({ label, value }) => (
    <div className="py-3 border-b border-gray-50 last:border-0">
        <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
        <p className="text-sm font-medium text-gray-800">{value || 'N/A'}</p>
    </div>
);

const DriverCar = () => {
    const { user } = useAuth();
    const driverId = user?.linked_entity_id;
    const [allocations, setAllocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!driverId) return;
        axios.get(`${rootUrl}/cars/drivers/driver/${driverId}`)
            .then(res => {
                const data = res.data?.data || [];
                setAllocations(data);
            })
            .catch(() => setError('Failed to load car allocation.'))
            .finally(() => setLoading(false));
    }, [driverId]);

    if (!driverId) return (
        <div className="min-h-screen flex items-center justify-center text-gray-500 pb-16">
            No driver account linked.
        </div>
    );

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center pb-16">
            <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    const active = allocations.find(a => !a.end_date);
    const history = allocations.filter(a => !!a.end_date).slice(0, 5);

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            <div className="bg-orange-500 px-4 pt-10 pb-6">
                <h1 className="text-white text-xl font-bold">My Car</h1>
            </div>

            <div className="px-4 py-4 space-y-4">
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-600 text-sm">
                        <HiExclamationCircle className="w-4 h-4 flex-shrink-0" />
                        {error}
                    </div>
                )}

                {active ? (
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <div className="bg-orange-50 px-4 py-3 flex items-center gap-2 border-b border-orange-100">
                            <HiTruck className="w-5 h-5 text-orange-500" />
                            <span className="font-semibold text-orange-700 text-sm">Current Allocation</span>
                        </div>
                        <div className="px-4">
                            <Field label="Plate Number" value={active.plate_no} />
                            <Field label="Make / Model" value={[active.car_make, active.car_model].filter(Boolean).join(' ') || null} />
                            <Field label="Start Date" value={fmt(active.start_date)} />
                            <Field label="Status" value="Active" />
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
                        <HiTruck className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                        <p className="text-gray-400 text-sm">No car currently allocated.</p>
                    </div>
                )}

                {history.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-50 flex items-center gap-2">
                            <HiCalendar className="w-4 h-4 text-gray-400" />
                            <h2 className="text-sm font-semibold text-gray-700">Previous Allocations</h2>
                        </div>
                        <ul className="divide-y divide-gray-50">
                            {history.map((a, i) => (
                                <li key={i} className="px-4 py-3">
                                    <p className="text-sm font-medium text-gray-800">{a.plate_no}</p>
                                    <p className="text-xs text-gray-400 mt-0.5">
                                        {fmt(a.start_date)} – {fmt(a.end_date)}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <DriverNav />
        </div>
    );
};

export default DriverCar;
